/**
 * Booking window: up to which visit date guests may book online.
 *
 * Dates are restaurant calendar dates (`YYYY-MM-DD`) and are compared as
 * strings, so neither UTC midnight nor a DST switch can move the boundary.
 *
 * Vendored verbatim into next-dashboard and skylab; keep the copies identical.
 */

export type BookingWindowMode = 'rolling' | 'fixed';

export type BookingWindowProblem = 'missing_date' | 'invalid_date' | 'unknown_mode';

export type BookingWindow =
  /** `lastBookableDate: null` means no limit. */
  | { mode: 'rolling'; lastBookableDate: string | null }
  | { mode: 'fixed'; lastBookableDate: string }
  /** A broken fixed configuration: guests can book nothing until it is repaired. */
  | { mode: 'invalid'; lastBookableDate: null; problem: BookingWindowProblem };

/** A booking window together with whether one particular date lies inside it. */
export type BookingWindowStatus = BookingWindow & { released: boolean };

/** `details.reason` of a Public API SLOT_NOT_AVAILABLE caused by the booking window. */
export const DATE_NOT_RELEASED_REASON = 'date_not_released';

export type BookingWindowSettings = {
  bookingWindowMode?: string | null;
  bookableUntil?: string | null;
  maxDaysInAdvance?: number | null;
};

/**
 * Reservation-settings fields only the booking-window publish endpoint writes.
 * Client settings publications carry the published values over unchanged
 * (enforced by firestore.rules).
 */
export const BOOKING_WINDOW_SERVER_FIELDS = [
  'bookingWindowMode',
  'bookableUntil',
  'maxDaysInAdvance',
  'bookingWindowRevision',
  'bookingWindowPublishedAt',
  'bookingWindowPublishedBy',
] as const;

/** Newly bookable visit dates, both ends inclusive; `to: null` is open-ended. */
export type ReleasedRange = { from: string; to: string | null };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoCalendarDate(value: unknown): value is string {
  if (typeof value !== 'string' || !ISO_DATE.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

export function addCalendarDays(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

export function resolveBookingWindow(
  settings: BookingWindowSettings,
  today: string
): BookingWindow {
  const mode = settings.bookingWindowMode;

  // Restaurants that never chose a mode keep the rolling horizon they had.
  if (mode === undefined || mode === null || mode === 'rolling') {
    const maxDaysInAdvance = Number(settings.maxDaysInAdvance);
    return {
      mode: 'rolling',
      lastBookableDate: maxDaysInAdvance ? addCalendarDays(today, maxDaysInAdvance) : null,
    };
  }

  if (mode !== 'fixed') return { mode: 'invalid', lastBookableDate: null, problem: 'unknown_mode' };

  if (settings.bookableUntil === undefined || settings.bookableUntil === null) {
    return { mode: 'invalid', lastBookableDate: null, problem: 'missing_date' };
  }

  if (!isIsoCalendarDate(settings.bookableUntil)) {
    return { mode: 'invalid', lastBookableDate: null, problem: 'invalid_date' };
  }

  return { mode: 'fixed', lastBookableDate: settings.bookableUntil };
}

export function isDateReleased(window: BookingWindow, date: string): boolean {
  if (window.mode === 'invalid') return false;
  return window.lastBookableDate === null || date <= window.lastBookableDate;
}

/**
 * Dates a change from `previous` to `next` opens for guests. Publishing the
 * same boundary again or shortening it releases nothing.
 */
export function newlyReleasedRange(
  previous: BookingWindow,
  next: BookingWindow,
  today: string
): ReleasedRange | null {
  if (next.mode === 'invalid') return null;

  let dayAfterPrevious: string;
  if (previous.mode === 'invalid') {
    dayAfterPrevious = today;
  } else if (previous.lastBookableDate === null) {
    return null;
  } else {
    dayAfterPrevious = addCalendarDays(previous.lastBookableDate, 1);
  }
  // Past days never become bookable, so a release starts today at the earliest.
  const from = dayAfterPrevious < today ? today : dayAfterPrevious;
  const to = next.lastBookableDate;

  if (to !== null && to < from) return null;

  return { from, to };
}

/**
 * Today's calendar date for the restaurant, independent of the device's own
 * timezone. Restaurants run on Europe/Berlin like the rest of the platform.
 */
export function restaurantToday(now: Date = new Date(), timeZone = 'Europe/Berlin'): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

const DATE_LOCALES: Record<string, string> = { de: 'de-DE', en: 'en-GB', fr: 'fr-FR', es: 'es-ES' };

export function formatBookingWindowDate(date: string, language: string): string {
  return new Intl.DateTimeFormat(DATE_LOCALES[language.slice(0, 2)] ?? 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));
}
